import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { Routes } from 'types/routes';

import { Gender, Genders } from 'models/Product';

import { withVariables } from 'utils/string';
import { getProvider } from 'services/Cookies';

import MarketContext from 'components/context/Market';

import FirstStep from './FirstStep';
import SecondStep from './SecondStep';
import StyledComponent from './styles';
import { FirstStepElement, Props, SecondStepElement } from './types';

const PublicLayoutNavigationMobileDropDownMenu: FunctionComponent<Props> = ({ isBannerVisible, translations, children }) => {
    const [activeGender, setActiveGender] = useState<Genders>(null);
    const [firstStepElement, setFirstStepElement] = useState<FirstStepElement>(null);
    const [secondStepElement, setSecondStepElement] = useState<SecondStepElement>(null);
    const { productCategories, locations } = useContext(MarketContext);
    const router = useRouter();

    const firstStepElementsNotEmpty = Boolean(Array.isArray(firstStepElement?.sections) && firstStepElement?.sections?.length > 0);
    const firstTranslate = firstStepElement?.toClose !== true && firstStepElementsNotEmpty;
    const secondTranslate = secondStepElement?.toClose !== true && Boolean(secondStepElement?.subsections?.length > 0);

    const visibleProductCategories = useMemo(() =>
        productCategories?.filter(category => category.showInMenu !== false) || [],
    [productCategories]);

    const locationsByCategory = useMemo(() => {
        if (!(locations?.length && visibleProductCategories?.length)) return {};

        const locationMap: {[categoryId: string]: any[]} = {};

        visibleProductCategories.forEach(category => {
            locationMap[category.id] = [];
        });

        locations.forEach(location => {
            location.productCategories?.forEach(category => {
                if (locationMap[category.id]) {
                    const hasVisibleLocale = location.locales?.some(
                        locale =>
                            locale.productCategoryId === category.id &&
                            locale.showInCategoryMenu !== false
                    );

                    if (hasVisibleLocale) {
                        locationMap[category.id].push(location);
                    }
                }
            });
        });

        return locationMap;
    }, [locations, visibleProductCategories]);

    const renderLocationsByCity = (locations: any[], categorySlug: string, categoryId: string) => {
        const locationsByCity: Record<string, any[]> = {};

        locations.forEach(location => {
            const visibleLocales = location.locales?.filter(
                locale => locale.showInCategoryMenu !== false
            );

            if (!visibleLocales?.length) return;

            const cityName = location.city?.name || 'Inne';
            if (!locationsByCity[cityName]) {
                locationsByCity[cityName] = [];
            }
            locationsByCity[cityName].push(location);
        });

        return Object.entries(locationsByCity).map(([cityName, cityLocations]) => ({
            key: cityName,
            title: cityName,
            subsections: cityLocations.map(location => {
                const locationLocale =
                    location.locales?.find(
                        locale => locale.productCategoryId === categoryId && locale.showInCategoryMenu !== false
                    ) ||
                    location.locales?.find(locale => locale.showInCategoryMenu !== false) ||
                    location.locales?.[0];

                return {
                    key: location.id,
                    title: locationLocale?.name || location.name,
                    subTitle: location.displayAddress,
                    href: withVariables(Routes.PublicCategoryLocation, {
                        categorySlug,
                        slug: locationLocale?.slug,
                    }),
                    query: { productCategoryId: categoryId },
                };
            }),
        }));
    };

    const locationSections = useMemo(() => {
        if (!visibleProductCategories?.length) {
            return [];
        }

        return visibleProductCategories
            .map(category => {
                const categoryLocations = locationsByCategory[category.id] || [];
                const locationSubsections = categoryLocations.length
                    ? renderLocationsByCity(
                        categoryLocations,
                        category.slug,
                        category.id,
                    )
                    : [];

                if (locationSubsections.length === 0) {
                    return null;
                }

                return {
                    key: category.name,
                    id: category.id,
                    title: category.nameDisplay,
                    slug: category.slug,
                    subsections: [
                        ...locationSubsections,
                        {
                            key: `all-${category.id}`,
                            title: 'Zobacz wszystkie lokalizacje',
                            highlighted: true,
                            href: withVariables(Routes.PublicLocations, {}),
                            query: { productCategoryId: category.id },
                        },
                    ],
                };
            })
            .filter(Boolean);
    }, [visibleProductCategories, locationsByCategory]);

    const processPageData = (page) => {
        if (page.key === 'locations' && page.dropDown?.dynamicContent) {
            return {
                ...page,
                dropDown: {
                    ...page.dropDown,
                    sections: locationSections,
                },
            };
        } else if (page.key === 'otherTreatmets' && page.subMenu?.dynamicContent) {
            return {
                ...page,
                subMenu: {
                    ...page.subMenu,
                    sections: visibleProductCategories.map(category => ({
                        key: category.id,
                        title: category.nameDisplay,
                        href: category.link || '/' + category.slug,
                    })),
                },
            };
        }
        return page;
    };

    const processedPages = useMemo(() => {
        return translations?.pages?.elements?.map(page => processPageData(page)) || [];
    }, [translations?.pages?.elements, locationSections, visibleProductCategories]);

    useEffect(() => {
        const usedGender = getProvider().get(Gender.gender);
        setActiveGender(usedGender as Genders);
    }, []);

    useEffect(() => {
        if (router.pathname === Routes.PublicWemanZone) setActiveGender(Genders.Female);
        if (router.pathname === Routes.PublicMenZone) setActiveGender(Genders.Male);
    }, [router.pathname]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (firstStepElement?.toClose) timeout = setTimeout(() => setFirstStepElement(null), 800);
        return () => clearTimeout(timeout);
    }, [firstStepElement]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (secondStepElement?.toClose) timeout = setTimeout(() => setSecondStepElement(null), 800);
        return () => clearTimeout(timeout);
    }, [secondStepElement]);

    return (
        <StyledComponent className={classnames('public-layout-navigation-mobile-drop-down-menu', { 'topbar': isBannerVisible })}>
            <div className={classnames('drop-down-wrapper', {
                'first-step-translate': firstTranslate,
                'second-step-translate': secondTranslate,
            })}
            >
                <div className="gender-zone-buttons">
                    {translations?.genderButtonsMobile?.buttonGroupProps?.buttons && (
                        translations?.genderButtonsMobile?.buttonGroupProps?.buttons.map((redirectButton) => (
                            <Link
                                key={redirectButton.children}
                                href={redirectButton.href}
                                prefetch={false}
                            >
                                <a
                                    className={classnames({
                                        'gender-button': true,
                                        'active': activeGender === redirectButton.key,
                                    }, redirectButton.key)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (redirectButton.key === Genders.Male) {
                                            getProvider().set(Gender.gender, Genders.Male);
                                            router.push(withVariables(Routes.PublicMenZone));
                                        }
                                        if (redirectButton.key === Genders.Female) {
                                            getProvider().set(Gender.gender, Genders.Female);
                                            router.push(withVariables(Routes.PublicWemanZone));
                                        }
                                    }}
                                >
                                    {redirectButton.children}
                                </a>
                            </Link>
                        ))
                    )}
                </div>
                <div className="pages">
                    {processedPages.map((page) => (
                        <div
                            key={page.key}
                            className={classnames(
                                'pages-link',
                                {
                                    'highlighted': page.buttonProps?.highlighted,
                                },
                            )}
                        >
                            {page?.buttonProps?.href
                                ? (
                                    <Link
                                        href={page.buttonProps.href}
                                        prefetch={false}
                                    >
                                        <a
                                            className="pages-link-anchor"
                                        >
                                            {page.mobileIcon && (
                                                <div className="mobile-icon">
                                                    <img
                                                        src={page.mobileIcon}
                                                        alt="icon"
                                                        width="auto"
                                                        height="26px"
                                                    />
                                                </div>
                                            )}
                                            {page.buttonProps.children}
                                            {page.subMenu && page.buttonProps.href !== Routes.PublicBlog && (
                                                <img
                                                    className="arrow-icon"
                                                    src="/images/menu/arrow.svg"
                                                    alt="icon"
                                                    width="100px"
                                                    height="auto"
                                                />
                                            )}
                                        </a>
                                    </Link>
                                )
                                : (
                                    <div
                                        className="pages-link-anchor"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setFirstStepElement({
                                                page,
                                                sections: page?.subMenu?.sections || page?.dropDown?.sections,
                                            });
                                        }}
                                    >
                                        {page.mobileIcon && (
                                            <div className="mobile-icon">
                                                <img
                                                    src={page.mobileIcon}
                                                    alt="icon"
                                                    width="auto"
                                                    height="26px"
                                                />
                                            </div>
                                        )}
                                        {page.buttonProps.children}
                                        {(page.subMenu || page.dropDown) && (
                                            <img
                                                className="arrow-icon"
                                                src="/images/menu/arrow.svg"
                                                alt="icon"
                                                width="100px"
                                                height="auto"
                                            />
                                        )}
                                    </div>
                                )
                            }
                        </div>
                    ))}
                    <a
                        className="offer-button"
                        href="/strefa-kobiet/konsultacja-online/"
                        rel="noopener"
                    >
                        <img
                            src="/theme/images/globe.svg"
                            aria-hidden="true"
                            alt="globe"
                            className="icon"
                            width={20}
                            height={20}
                        />
                        <p className="offer-button-text">
                            Konsultacja online z kosmetologiem
                        </p>
                    </a>
                </div>
                <FirstStep
                    className={classnames({
                        'active': Array.isArray(firstStepElement?.sections) && firstStepElement?.sections?.length > 0,
                    })}
                    firstStepElement={firstStepElement}
                    setFirstStepElement={setFirstStepElement}
                    setSecondStepElement={setSecondStepElement}
                />
                <SecondStep
                    firstStepElement={firstStepElement}
                    secondStepElement={secondStepElement}
                    setSecondStepElement={setSecondStepElement}
                />
            </div>
            <div className="info-line">
                {children}
            </div>
        </StyledComponent>
    );
};

export default PublicLayoutNavigationMobileDropDownMenu;
